import { useState } from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ContactQuoteStepper from "./ContactQuoteStepper";
import { useContactModal } from "@/components/ContactModalProvider";
import RichText from "@/components/RichText";

// Valeurs par défaut identiques au contenu d'origine — reprises ici pour
// que la section reste utilisable même si le backend ne renvoie pas
// encore de props (ex: pendant le premier chargement).
const DEFAULTS = {
  heading: "Parlons de votre projet.",
  subheading: "Décrivez votre besoin, on revient vers vous sous 24h avec une estimation concrète.",
  email: "devis@clickexpert.fr",
  phone: "+33 6 69 46 14 77",
  address: "Paris",
  // "simple" = comportement d'origine (formulaire unique, ci-dessous) ;
  // "stepper" affiche à la place ContactQuoteStepper (ex: thème "Assurance",
  // demande de devis en plusieurs étapes) — voir DEFAULT_PROPS.contact côté
  // backend (theme_service.py).
  form_style: "simple",
  quote_options: "Auto, Habitation, Santé, Prévoyance",
};

export default function ContactSection({ props = {} }) {
  const t = { ...DEFAULTS, ...props };
  const telHref = `tel:${(t.phone || "").replace(/[^\d+]/g, "")}`;
  const [homeContact, setHomeContact] = useState({ name: "", email: "", type: "", message: "" });
  const [homeContactSending, setHomeContactSending] = useState(false);
  const { notifySent } = useContactModal();
  const setHomeField = (k) => (e) => setHomeContact((f) => ({ ...f, [k]: e.target.value }));

  const submitHomeContact = async (e) => {
    e.preventDefault();
    setHomeContactSending(true);
    try {
      await api.post("/contact-request", {
        name: homeContact.name,
        email: homeContact.email,
        message: homeContact.type
          ? `Type de projet : ${homeContact.type}\n\n${homeContact.message}`
          : homeContact.message,
        source: "home",
      });
      notifySent("", { name: homeContact.name });
      setHomeContact({ name: "", email: "", type: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'envoi, réessayez.");
    } finally {
      setHomeContactSending(false);
    }
  };

  return (
    <section id="contact" className="max-w-[1240px] mx-auto px-6 md:px-10 pt-section-top md:pt-section-top-md pb-4 scroll-mt-28">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div>
          <RichText
            as="h2"
            className="font-display font-semibold text-[30px] md:text-[34px] leading-tight tracking-[-0.02em] text-ink"
            html={t.heading}
          />
          <RichText as="p" preserveParagraphs className="text-[17px] text-graphite mt-4 max-w-md" html={t.subheading} />
          <div className="mt-9 space-y-4">
            <a href={`mailto:${t.email}`} className="flex items-center gap-3 text-[15px] text-ink hover:text-gold transition-colors">
              <span className="h-9 w-9 rounded-btn bg-white border-control border-stone flex items-center justify-center"><Mail className="h-4 w-4 text-gold" /></span>
              {t.email}
            </a>
            <a href={telHref} className="flex items-center gap-3 text-[15px] text-ink hover:text-gold transition-colors">
              <span className="h-9 w-9 rounded-btn bg-white border-control border-stone flex items-center justify-center"><Phone className="h-4 w-4 text-gold" /></span>
              {t.phone}
            </a>
            <div className="flex items-center gap-3 text-[15px] text-graphite">
              <span className="h-9 w-9 rounded-btn bg-white border-control border-stone flex items-center justify-center"><MapPin className="h-4 w-4 text-gold" /></span>
              {t.address}
            </div>
          </div>
        </div>

        {t.form_style === "stepper" ? (
          <ContactQuoteStepper quoteOptions={t.quote_options} fieldSettings={t} />
        ) : (
          <form
            data-testid="contact-form"
            onSubmit={submitHomeContact}
            className="rounded-card border-control border-stone bg-white p-6 md:p-8 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Nom" data-testid="contact-name" value={homeContact.name} onChange={setHomeField("name")} className="w-full rounded-btn border-control border-stone bg-input-fill px-4 py-3 text-[14px] text-ink outline-none focus:border-gold focus:bg-white transition-colors" />
              <input required type="email" placeholder="Email professionnel" data-testid="contact-email" value={homeContact.email} onChange={setHomeField("email")} className="w-full rounded-btn border-control border-stone bg-input-fill px-4 py-3 text-[14px] text-ink outline-none focus:border-gold focus:bg-white transition-colors" />
            </div>
            <input placeholder="Type de projet" data-testid="contact-type" value={homeContact.type} onChange={setHomeField("type")} className="w-full rounded-btn border-control border-stone bg-input-fill px-4 py-3 text-[14px] text-ink outline-none focus:border-gold focus:bg-white transition-colors" />
            <textarea required rows={5} placeholder="Décrivez votre besoin…" data-testid="contact-message" value={homeContact.message} onChange={setHomeField("message")} className="w-full rounded-btn border-control border-stone bg-input-fill px-4 py-3 text-[14px] text-ink outline-none focus:border-gold focus:bg-white transition-colors resize-none" />
            <button type="submit" disabled={homeContactSending} data-testid="contact-submit" className="w-full bg-ink text-white py-3.5 rounded-btn text-[14px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
              {homeContactSending ? "Envoi en cours…" : "Envoyer la demande"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
